/**
 * Team management — owner invites VAs by email. Each VA gets a placeholder
 * users row in our database (clerk_user_id = NULL). When the VA signs up via
 * Clerk with that email, /api/me's auto-link path claims the placeholder and
 * stamps their Clerk user id onto the existing row.
 *
 * Until they sign in, the row shows as "Pending" in the table.
 */

import { useEffect, useState } from 'react';

import { AppShell } from '../components/AppShell';
import { useApi, ApiError } from '../lib/api';

type TeamRow = {
  id: string;
  email: string;
  clerk_user_id: string | null;
  role: 'owner' | 'va' | 'va_admin' | 'client';
  created_at: number;
  last_login_at: number | null;
};

export function Team() {
  const { request } = useApi();
  const [team, setTeam] = useState<TeamRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteErr, setInviteErr] = useState<string | null>(null);
  const [justInvited, setJustInvited] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const res = await request<{ team: TeamRow[] }>('/admin/team');
      setTeam(res.team);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviting(true);
    setInviteErr(null);
    setJustInvited(null);
    try {
      const res = await request<{ invited: { email: string } }>(
        '/admin/team/invite',
        {
          method: 'POST',
          body: JSON.stringify({ email: email.trim(), name: name.trim() }),
        },
      );
      setJustInvited(res.invited.email);
      setEmail('');
      setName('');
      await refresh();
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { detail?: string; error?: string } | null;
        setInviteErr(body?.detail ?? body?.error ?? (e as Error).message);
      } else {
        setInviteErr((e as Error).message);
      }
    } finally {
      setInviting(false);
    }
  }

  async function toggleRole(row: TeamRow) {
    if (row.role !== 'va' && row.role !== 'va_admin') return;
    const next = row.role === 'va_admin' ? 'va' : 'va_admin';
    const niceNext = next === 'va_admin' ? 'Admin' : 'Limited';
    if (!confirm(`Set ${row.email} access level to ${niceNext}?`)) return;
    try {
      await request(`/admin/team/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: next }),
      });
      await refresh();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function removeVa(row: TeamRow) {
    if (row.role === 'owner') return;
    if (!confirm(`Remove ${row.email} from the agency?`)) return;
    try {
      await request(`/admin/team/${row.id}`, { method: 'DELETE' });
      await refresh();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  return (
    <AppShell userRole="owner">
      <main className="page">
        <div className="page-hero">
          <div className="page-hero-icon" aria-hidden="true">🤝</div>
          <div className="page-hero-text">
            <h1 className="page-hero-title">Team</h1>
            <p className="page-hero-sub">
              Invite virtual assistants to help manage clients. They&apos;ll see
              the same admin views you see, scoped to whichever clients you
              assign them.
            </p>
          </div>
        </div>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Invite a VA</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Enter their email and have them sign up at{' '}
            <code>app.grouppulsehq.com</code> using that same email. As soon
            as they do, their account links automatically.
          </p>
          <form
            onSubmit={handleInvite}
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr auto',
              gap: 10,
              alignItems: 'end',
              maxWidth: 760,
            }}
          >
            <Field label="Email">
              <input
                type="email"
                required
                placeholder="va@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
                autoComplete="off"
              />
            </Field>
            <Field label="Name (optional)">
              <input
                type="text"
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
                autoComplete="off"
              />
            </Field>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={inviting || !email.trim()}
            >
              {inviting ? 'Inviting…' : 'Send invite'}
            </button>
          </form>
          {inviteErr && (
            <p style={{ color: '#b91c1c', marginTop: 10, fontSize: 14 }}>
              {inviteErr}
            </p>
          )}
          {justInvited && (
            <p style={{ color: '#047857', marginTop: 10, fontSize: 14 }}>
              ✓ Invited {justInvited} — they&apos;ll appear in the table below
              and become active once they sign in.
            </p>
          )}
        </section>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Current team</h2>
          {loading ? (
            <p className="muted">Loading…</p>
          ) : err ? (
            <p style={{ color: '#b91c1c' }}>{err}</p>
          ) : !team || team.length === 0 ? (
            <p className="muted">No team members yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Member</th>
                    <th style={thStyle}>Role</th>
                    <th style={thStyle}>Access level</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Last sign-in</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {team.map((row) => {
                    const pending =
                      !row.clerk_user_id ||
                      row.clerk_user_id.startsWith('pending_');
                    const isVa = row.role === 'va' || row.role === 'va_admin';
                    return (
                      <tr key={row.id} style={trStyle}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500 }}>{row.email}</div>
                        </td>
                        <td style={tdStyle}>
                          <Pill color={row.role === 'owner' ? 'purple' : 'indigo'}>
                            {row.role === 'va_admin' ? 'VA' : row.role}
                          </Pill>
                        </td>
                        <td style={tdStyle}>
                          {row.role === 'owner' ? (
                            <Pill color="purple">Owner (full)</Pill>
                          ) : isVa ? (
                            <button
                              type="button"
                              onClick={() => void toggleRole(row)}
                              title="Click to toggle"
                              style={accessBtnStyle}
                            >
                              {row.role === 'va_admin' ? (
                                <Pill color="green">Admin · click to limit</Pill>
                              ) : (
                                <Pill color="yellow">Limited · click to promote</Pill>
                              )}
                            </button>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {pending ? (
                            <Pill color="yellow">Pending sign-in</Pill>
                          ) : (
                            <Pill color="green">Active</Pill>
                          )}
                        </td>
                        <td style={tdStyle} className="muted">
                          {row.last_login_at
                            ? new Date(row.last_login_at).toLocaleString()
                            : '—'}
                        </td>
                        <td style={tdStyle}>
                          {isVa && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => void removeVa(row)}
                              style={{ fontSize: 13, padding: '6px 10px' }}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p
                className="muted"
                style={{ fontSize: 13, marginTop: 14, marginBottom: 0 }}
              >
                <strong>Limited</strong> — VA only sees leads for clients you
                explicitly assign to them.{' '}
                <strong>Admin</strong> — VA can manage every client&apos;s
                profile, keywords, watched groups, and leads. They can&apos;t
                generate Stripe checkout links, manage the team, or change
                agency-level settings.
              </p>
            </div>
          )}
        </section>
      </main>
    </AppShell>
  );
}

// -- bits --------------------------------------------------------------------
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
function Pill({
  color,
  children,
}: {
  color: 'green' | 'yellow' | 'indigo' | 'purple';
  children: React.ReactNode;
}) {
  const palette: Record<string, { bg: string; fg: string }> = {
    green: { bg: '#d1fae5', fg: '#065f46' },
    yellow: { bg: '#fef3c7', fg: '#92400e' },
    indigo: { bg: '#e0e7ff', fg: '#3730a3' },
    purple: { bg: '#ede9fe', fg: '#5b21b6' },
  };
  const c = palette[color]!;
  return (
    <span
      style={{
        display: 'inline-block',
        background: c.bg,
        color: c.fg,
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {children}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  background: '#fff',
  color: 'var(--text)',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--muted)',
  fontWeight: 500,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};
const trStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--border)',
};
const tdStyle: React.CSSProperties = {
  padding: '12px',
  verticalAlign: 'top',
};
const accessBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  font: 'inherit',
};
